import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { apiError, brl, parseMoney, maskDoc, printAgent, effectivePrice, rows } from "@/lib/api";
import useAgenteDeImpressao from "@/hooks/useAgenteDeImpressao";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, User } from "lucide-react";
import { Modal, ConfirmDialog } from "@/components/panel/ui";
import BarcodeScanner from "@/components/BarcodeScanner";
import CustomerPicker from "@/components/panel/pickers/CustomerPicker";
import { Button, Field, Input, Select } from "@/components/ui-kit";
import { buildReceiptHtml } from "./pos/receipt";
import ProductSearch, { isBarcode } from "./pos/ProductSearch";
import CartList from "./pos/CartList";
import PaymentSection from "./pos/PaymentSection";
import DiscountField from "./pos/DiscountField";
import { SaleTotals, MobileActionBar } from "./pos/SaleActions";
import { discountAmount, discountExceedsCap } from "./pos/discount";
import { lerOuCriarClientUuid, rotacionarClientUuid } from "./pos/clientUuid";
import {
  contarFalhas, contarPendentes, ehFalhaDeRede, enfileirar, enviarPendentes,
  lerFila, reenfileirar, removerDaFila,
} from "./pos/offlineQueue";
import { cartFromItems, useRepeatPurchase } from "./pos/useRepeatPurchase";
import { SaveHeldSaleModal, HeldSalesModal, ResumeWarnings } from "./pos/HeldSales";
import CustomerHistory from "./pos/CustomerHistory";
import useHeldSales from "./pos/useHeldSales";
import AddItemDialog from "./pos/AddItemDialog";

/* Leitor de código de barras: componente compartilhado em @/components/BarcodeScanner */

/* Cadastro rápido de cliente: virou parte do CustomerPicker, para que OS,
   orçamento e aluguel também consigam cadastrar sem sair do formulário. */

/** Título do aviso quando o carrinho veio de uma compra anterior, não de uma venda guardada. */
const AVISO_RECOMPRA = "Mudou desde a compra anterior";

export default function POS() {
  const { user, can } = useAuth();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState("");
  const [discountType, setDiscountType] = useState("valor");
  // Frete cobrado na entrega. Texto, como o desconto: o vendedor digita "35,00"
  // e a conversão para número acontece num lugar só.
  const [shipping, setShipping] = useState("");
  /* A quem a venda pertence. Vazio = quem está logado, que é o caso normal.
     Só aparece para quem tem a permissão `vendedores` — a mesma que já define
     meta e taxa. Sem essa trava um vendedor poderia empurrar venda para o
     colega, ou tirar do dele. */
  const podeEscolherVendedor = can("vendedores");
  const [sellerId, setSellerId] = useState("");
  const [equipe, setEquipe] = useState([]);
  const [lastSale, setLastSale] = useState(null);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [busy, setBusy] = useState(false); // trava anti-duplo-envio (finalizar / emitir NF-e)
  const [emitPrompt, setEmitPrompt] = useState(null); // { saleId, number } quando a venda não emitiu NFC-e sozinha
  const [emitDoc, setEmitDoc] = useState("");
  const [emitting, setEmitting] = useState(false);
  const [flashKey, setFlashKey] = useState(null);
  // A ponte de impressão desta máquina — ver o hook para por que o aviso só
  // aparece onde ela já respondeu alguma vez.
  const agente = useAgenteDeImpressao();
  const [scanning, setScanning] = useState(false);
  const [cashOpen, setCashOpen] = useState(null); // null = ainda não sei
  // O PDV vive em /admin/pdv, /vendedor e /balcao; a frente de caixa é irmã dele.
  const location = useLocation();
  const { pathname } = location;
  const cashRegisterPath = `/${pathname.split("/")[1] || "admin"}/frente-caixa`;
  // cliente (busca e cadastro rápido ficam no CustomerPicker)
  const [customer, setCustomer] = useState(null);
  const [manualDoc, setManualDoc] = useState("");
  // pagamentos
  const [payments, setPayments] = useState([]);
  const [received, setReceived] = useState("");
  const [resumeWarnings, setResumeWarnings] = useState([]);
  // O aviso do que mudou serve a dois caminhos — retomar a venda guardada e
  // repetir uma compra anterior — e a frase muda com o caminho: dizer "desde
  // que você guardou" numa recompra é avisar o certo com a história errada.
  const [warningsTitle, setWarningsTitle] = useState(null);
  // Confirmações do carrinho: escolher o item e tirar o item são as duas ações
  // que geram cupom errado quando o dedo escorrega — e as duas eram imediatas.
  const [addPrompt, setAddPrompt] = useState(null);   // { product, variant }
  const [removePrompt, setRemovePrompt] = useState(null); // item do carrinho
  const barcodeRef = useRef(null);
  const discountRef = useRef(null);
  const checkoutRef = useRef(null);

  useEffect(() => { barcodeRef.current?.focus(); }, []);

  // Venda exige caixa aberto. A checagem é feita ao abrir a tela para o vendedor
  // saber antes de montar o carrinho, e não só no clique de finalizar.
  useEffect(() => {
    api.get("/pos/cash-session/current")
      .then((r) => setCashOpen(!!r.data?.session))
      .catch(() => setCashOpen(null)); // indeterminado: não bloqueia por falha de rede
  }, []);

  useRepeatPurchase(location, {
    setCart, setCustomer,
    setWarnings: (itens) => { setResumeWarnings(itens); setWarningsTitle(AVISO_RECOMPRA); },
    onLoaded: () => { clientUuidRef.current = rotacionarClientUuid(); },
  });

  useEffect(() => {
    if (!term || isBarcode(term)) { setResults([]); return; }
    // Busca do BALCÃO, não a da loja: o catálogo público esconde as categorias
    // marcadas como fora da loja virtual, e peça de reposição e item de uso
    // interno são vendidos aqui todo dia. Ver CatalogController::posSearch.
    const t = setTimeout(() => {
      api.get("/pos/products/search", { params: { search: term, per_page: 6 } })
        .then((r) => setResults(rows(r)))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [term]);

  /**
   * Entra no carrinho — SEMPRE no topo.
   *
   * O item novo ia para o fim da lista, que é onde o vendedor não está olhando:
   * numa venda de dez itens, conferir o que acabou de entrar exigia rolar até
   * embaixo com o cliente esperando. Item recém-tocado no topo é também o que
   * mantém o realce de entrada visível sem rolagem.
   *
   * Falta de estoque NÃO impede: o balcão vende o que está na prateleira, e o
   * saldo negativo é a entrada que ainda não foi lançada (ver a migration que
   * removeu o CHECK stock >= 0).
   */
  const addToCart = (product, variant, qty = 1) => {
    const key = `${product.id}:${variant.id}`;
    setCart((prev) => {
      const found = prev.find((i) => i.key === key);
      // Repetir um item também o traz para o topo: é o mesmo princípio, e sem
      // isso o "+1" de um item antigo acontecia fora da vista.
      if (found) return [{ ...found, qty: found.qty + qty }, ...prev.filter((i) => i.key !== key)];

      return [{ key, product_id: product.id, variant_id: variant.id, name: product.name,
        // A marca viaja com a linha: o carrinho e o cupom mostram "Mercur ·
        // Único", que é como o cliente confere o que está levando.
        brand: product.brand || null,
        // A foto também: é o que o cliente reconhece na tela do caixa. Vendas
        // em espera e a fila offline guardam a linha inteira, então a foto
        // sobrevive a um F5 junto com o resto.
        image: product.image || null,
        variant_label: variant.label, price: effectivePrice(product, variant), qty }, ...prev];
    });
    setFlashKey(key);
    setTimeout(() => setFlashKey((k) => (k === key ? null : k)), 600);
  };

  /**
   * Escolha de item pela BUSCA: passa pela confirmação.
   *
   * O código de barras não passa (ver lookupBarcode): o bipe já é a
   * confirmação, e um modal por leitura dobraria as ações numa fila.
   */
  const promptAdd = (product, variant) => setAddPrompt({ product, variant });

  const confirmAdd = (product, variant, qty) => {
    addToCart(product, variant, qty);
    setAddPrompt(null);
    setTerm(""); setResults([]);
    barcodeRef.current?.focus();
  };

  const changeQty = (key, delta) => setCart((prev) => prev.map((i) => (i.key === key ? { ...i, qty: i.qty + delta } : i)).filter((i) => i.qty > 0));

  /**
   * Abatimento de uma linha.
   *
   * Guarda o TEXTO digitado (`discountInput`) junto do valor: sem ele, digitar
   * "1," reformataria o campo no meio da digitação e o cursor pularia. O valor
   * numérico fica limitado ao bruto da linha, que é o mesmo limite do servidor.
   */
  const changeItemDiscount = (key, texto) => setCart((prev) => prev.map((i) => {
    if (i.key !== key) return i;
    const bruto = i.price * i.qty;

    return { ...i, discountInput: texto, discount: Math.min(bruto, Math.max(0, parseMoney(texto))) };
  }));

  /**
   * Leitura do código de barras: entra na venda direto, com ou sem saldo.
   *
   * Bipar é a confirmação — o produto está na mão de quem leu. Sem saldo o item
   * entra igual e o aviso é informativo: o vendedor não pode ser interrompido
   * por causa de um lançamento de entrada que o estoquista ainda não fez.
   */
  const lookupBarcode = useCallback(async (code) => {
    try {
      const { data } = await api.get(`/products/barcode/${code}`);
      addToCart(data.product, data.variant);
      if (data.variant.stock <= 0) {
        toast.warning(`${data.product.name} adicionado — sem saldo no cadastro`, {
          description: "A venda sai normalmente. O estoque fica negativo até a entrada ser lançada.",
        });
      } else {
        toast.success(`${data.product.name} adicionado`);
      }
    } catch (err) { toast.error(apiError(err)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitTerm = async (e) => {
    e.preventDefault();
    const value = term.trim();
    if (!value) return;
    if (isBarcode(value)) { await lookupBarcode(value); setTerm(""); return; }
    // Enter numa busca que deu um resultado único abre a confirmação, não
    // adiciona direto: continua sendo escolha por nome, e é aí que o produto
    // parecido entra na venda sem ninguém olhar.
    if (results.length === 1 && results[0].variants.length === 1) {
      promptAdd(results[0], results[0].variants[0]);
    }
  };

  const onScanDetect = useCallback((code) => { setScanning(false); lookupBarcode(String(code).trim()); }, [lookupBarcode]);

  /* A equipe é buscada uma vez, e falha em silêncio: sem ela o select some e a
     venda continua saindo no nome de quem está logado — que é o comportamento
     de sempre. O PDV não pode parar por causa de um campo de conveniência. */
  useEffect(() => {
    if (!podeEscolherVendedor) return;
    api.get("/admin/sellers").then((r) => setEquipe(rows(r))).catch(() => {});
  }, [podeEscolherVendedor]);

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  // Abatimento das linhas, somado à parte. O desconto GERAL é calculado sobre o
  // subtotal bruto (é o que o vendedor tem na cabeça ao dizer "10% na venda"),
  // e o total tira os dois — mesma conta do servidor.
  const itemDiscounts = cart.reduce((s, i) => s + (i.discount || 0), 0);
  const saleDiscountVal = discountAmount({ type: discountType, value: discount, subtotal });
  const discountVal = Math.min(subtotal, itemDiscounts + saleDiscountVal);
  // O teto do perfil é conferido AQUI, enquanto o vendedor digita, e sobre a
  // SOMA dos dois níveis — igual ao servidor. Olhar só o desconto geral deixaria
  // a tela aprovar o que a API vai recusar no clique de finalizar.
  const discountBlocked = discountExceedsCap({
    type: "valor", value: discountVal, subtotal, maxPercent: user?.max_discount_percent,
  });
  /* Frete entra POR FORA do desconto — é cobrança de entrega, não mercadoria.
     A ordem é a mesma do servidor: (subtotal − descontos) + frete. Fica de fora
     do teto de desconto do perfil de propósito: cobrar entrega não é abater. */
  const shippingVal = Math.max(0, parseMoney(shipping));
  const total = subtotal - discountVal + shippingVal;
  const paid = payments.reduce((s, p) => s + (parseMoney(p.amount)), 0);
  const remaining = Math.round((total - paid) * 100) / 100;
  const cashPortion = payments.filter((p) => p.method === "dinheiro").reduce((s, p) => s + (parseMoney(p.amount)), 0);
  const receivedVal = parseMoney(received);
  const change = cashPortion > 0 && receivedVal > 0 ? receivedVal - cashPortion : null;
  const paymentsSettled = payments.length > 0 && Math.abs(remaining) <= 0.01;

  const addPayment = (method) => setPayments((prev) => [...prev, { method, amount: Math.max(0, remaining).toFixed(2), installments: method === "cartao_credito" ? 1 : null }]);
  const setPayment = (idx, key, value) => setPayments((prev) => prev.map((p, i) => (i === idx ? { ...p, [key]: value } : p)));
  const removePayment = (idx) => setPayments((prev) => prev.filter((_, i) => i !== idx));

  /**
   * Identidade da tentativa de venda, gerada ANTES de enviar.
   *
   * É o mesmo valor em toda retentativa do MESMO carrinho, e só muda quando a
   * venda fecha (em resetSale). É isso que deixa o servidor reconhecer o reenvio
   * e devolver a venda já gravada em vez de criar outra — o caso em que a
   * resposta se perdia depois do commit e o estoque baixava duas vezes.
   */
  const clientUuidRef = useRef(lerOuCriarClientUuid());

  /* Vendas que a rede não deixou subir. Vive em localStorage, então sobrevive ao
     F5 e a fechar o navegador — o vendedor não perde a venda por causa de um
     pisco de Wi-Fi. O estado aqui é só o espelho para a tela. */
  const [fila, setFila] = useState(() => lerFila());
  const [descartePrompt, setDescartePrompt] = useState(null);
  const pendentes = contarPendentes(fila);
  const recusadas = contarFalhas(fila);

  const resetSale = () => {
    setCart([]); setDiscount(""); setDiscountType("valor"); setShipping(""); setPayments([]); setReceived("");
    // O vendedor NÃO é limpo: no balcão a mesma pessoa costuma atender várias
    // vendas seguidas, e zerar obrigaria a reescolher a cada cupom.
    setCustomer(null); setManualDoc(""); setResumeWarnings([]); setWarningsTitle(null);
    clientUuidRef.current = rotacionarClientUuid();   // próxima venda, nova identidade
  };

  /** Substitui a venda atual por um carrinho vindo de fora (retomada). */
  const applyCart = ({ cart: itens, discountType: tipo, discount: valor, customer: cli, manualDoc: doc, payments: pagos, warnings }) => {
    setCart(itens);
    setDiscountType(tipo); setDiscount(valor);
    /* O frete NÃO volta com a venda retomada: o carrinho guardado não o
       armazena (não há coluna em pos_held_sales), e herdar o que estava
       digitado na tela cobraria entrega de uma venda que não é a mesma.
       Zerar obriga a relançar de propósito. */
    setShipping("");
    setCustomer(cli); setManualDoc(doc);
    // O acerto combinado volta com a venda. `received` NÃO volta: dinheiro na
    // mão é do momento do fechamento, e restaurá-lo mostraria um troco de uma
    // cédula que ninguém entregou.
    setPayments(pagos || []); setReceived("");
    setResumeWarnings(warnings); setWarningsTitle(null);   // retomada: o título padrão é o certo
    // Carrinho novo, identidade nova: o uuid da venda anterior não vale para este.
    clientUuidRef.current = rotacionarClientUuid();
  };

  /**
   * Traz uma compra anterior do cliente para a venda EM CURSO.
   *
   * SOMA ao carrinho, não substitui: aqui o cliente é identificado no meio do
   * atendimento, muitas vezes com peça já bipada, e trocar o carrinho apagaria
   * o que está na mão do vendedor. (A recompra vinda do CRM abre o PDV vazio, e
   * por isso aquela pode substituir.) Item repetido soma quantidade e sobe para
   * o topo — mesma regra do `addToCart`, para o que acabou de entrar ficar onde
   * o vendedor está olhando.
   */
  const addPurchaseToCart = (data) => {
    const itens = cartFromItems(data.items);
    if (!itens.length) {
      toast.error("Os produtos desta compra não estão mais no catálogo.");
      return;
    }
    setCart((prev) => {
      const vindos = itens.map((novo) => {
        const atual = prev.find((i) => i.key === novo.key);

        return atual ? { ...atual, qty: atual.qty + novo.qty } : novo;
      });
      const chaves = new Set(vindos.map((i) => i.key));

      return [...vindos, ...prev.filter((i) => !chaves.has(i.key))];
    });
    // O que mudou de preço ou de saldo desde aquela compra usa o mesmo aviso da
    // retomada: é a mesma pergunta — o que renegociar antes de falar o valor.
    setResumeWarnings(data.items); setWarningsTitle(AVISO_RECOMPRA);
    barcodeRef.current?.focus();
    toast.success("Compra anterior adicionada. Confira o que mudou antes de fechar.");
  };

  const {
    held, heldOpen, setHeldOpen, saveOpen, setSaveOpen,
    holding, resumingId, saveHeld, resumeHeld, removeHeld,
  } = useHeldSales({
    cart, customer, manualDoc, discountType, discount, payments, resetSale, applyCart,
    onFocus: () => barcodeRef.current?.focus(),
  });

  /**
   * Retomada vinda de Vendas & Orçamentos ("Retomar no PDV").
   *
   * Aquele botão só trocava de tela: o PDV abria vazio e o vendedor precisava
   * caçar a mesma venda de novo na lista "Em aberto". Aqui o id chega pelo
   * estado da rota e a retomada é a MESMA de sempre (`resumeHeld`), que
   * reconfere preço e saldo contra o catálogo de agora e tira o rascunho da
   * lista — nada de um segundo caminho paralelo que pudesse divergir.
   *
   * A trava por ref existe porque `resumeHeld` muda o estado do PDV e o efeito
   * rodaria de novo antes do `replaceState` valer: sem ela, a mesma venda era
   * retomada duas vezes e a segunda batia em 404 (a primeira já a descartou).
   */
  const retomadaRef = useRef(null);
  useEffect(() => {
    const id = location.state?.resumeHeldId;
    if (!id || retomadaRef.current === id) return;
    retomadaRef.current = id;
    // Limpa o estado ANTES de retomar: um F5 no meio não deve remontar o
    // carrinho por cima do que o vendedor já mexeu.
    window.history.replaceState({}, "");
    resumeHeld({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  /**
   * Retomada de ORÇAMENTO ("Retomar no PDV" na tela de Vendas & Orçamentos).
   *
   * Diferente da venda em aberto, o orçamento CONTINUA existindo depois disto —
   * ele não é um rascunho de balcão, é uma proposta enviada ao cliente, e some
   * da lista só quando vira venda (convert) ou é marcado como pago. Por isso não
   * há `delete` aqui: se o vendedor abandonar o carrinho, a proposta permanece.
   *
   * Os itens já vêm revalidados pelo servidor (mesmo CartRevalidationService da
   * venda em aberto), então preço e saldo são os de agora e os avisos aparecem
   * igual.
   */
  const orcamentoRef = useRef(null);
  useEffect(() => {
    const orc = location.state?.resumeQuote;
    if (!orc?.id || orcamentoRef.current === orc.id) return;
    orcamentoRef.current = orc.id;
    window.history.replaceState({}, "");

    const itens = cartFromItems(orc.items);
    if (!itens.length) {
      toast.error("Os produtos deste orçamento não existem mais no catálogo.");
      return;
    }
    applyCart({
      cart: itens,
      discountType: "valor",
      discount: "",
      customer: orc.customer_id
        ? { id: orc.customer_id, name: orc.customer_name, phone: orc.phone }
        : null,
      manualDoc: "",
      payments: [],
      warnings: orc.items || [],
    });
    toast.success(`Orçamento ${orc.number} aberto no balcão. Confira preço e estoque antes de fechar.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const emitNfe = async () => {
    if (!lastSale || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pos/invoices/emit", { pos_sale_id: lastSale.id, type: "nfe" });
      toast.success(`NF-e ${data.number} ${data.status === "processando" ? "enviada para autorização" : "emitida"}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  /**
   * Guarda a venda que a rede não deixou subir e libera o balcão.
   *
   * O carrinho é limpo de propósito: o próximo cliente já está na fila, e a
   * venda que ficou para trás não depende mais da tela — ela está no
   * localStorage com a identidade dela. O cupom NÃO sai agora: a venda ainda não
   * tem número nem NFC-e, e um cupom sem número é um papel que não corresponde a
   * nada. Ele sai sozinho no instante em que a venda entra.
   */
  const segurarVenda = (corpo, receipt) => {
    try {
      enfileirar({ payload: corpo, receipt });
    } catch (e) {
      // Fila cheia: a venda CONTINUA na tela. Limpar aqui perderia o carrinho
      // sem ter guardado nada.
      toast.error(e.message, { duration: 12000 });
      return;
    }
    setFila(lerFila());
    resetSale();
    toast.warning(
      "Sem conexão com o servidor. A venda ficou em espera e sobe sozinha quando a rede voltar — o cupom sai nessa hora.",
      { duration: 12000 },
    );
    barcodeRef.current?.focus();
  };

  const finalize = async () => {
    if (busy || cart.length === 0) return;
    // O atalho F2 não passa pelo botão desabilitado, então a trava vem aqui também.
    if (cashOpen === false) { toast.error("Caixa fechado. Abra o caixa na Frente de Caixa para registrar vendas."); return; }
    if (discountBlocked) { toast.error("Desconto acima do limite do seu perfil. Ajuste antes de finalizar."); return; }
    if (payments.length === 0) { toast.error("Adicione a forma de pagamento"); return; }
    if (Math.abs(remaining) > 0.01) { toast.error(`Faltam ${brl(remaining)} nos pagamentos`); return; }
    // Snapshot do cupom ANTES de limpar a venda (para reimpressão do comprovante).
    const receipt = {
      customer: customer?.name || null,
      items: cart.map((i) => ({ name: i.name, variant: i.variant_label, qty: i.qty, price: i.price, discount: i.discount || 0 })),
      subtotal, discount: discountVal, shipping: shippingVal, total,
      payments: payments.map((p) => ({ method: p.method, amount: parseMoney(p.amount) })),
      received: cashPortion > 0 ? receivedVal : 0,
      change: change != null && change > 0 ? change : 0,
    };
    const saleDoc = customer?.document || (manualDoc ? manualDoc.replace(/\D/g, "") : "");

    // Só a chamada de rede fica no try: qualquer falha daqui para baixo é da UI,
    // e a venda JÁ existe no servidor. Tratá-las como "erro de venda" fazia o
    // vendedor ver toast de erro em venda concluída — sem cupom e sem NFC-e.
    const corpo = {
      client_uuid: clientUuidRef.current,
      items: cart.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty, discount: i.discount || 0 })),
      customer_id: customer?.id || null,
      customer_name: customer?.name || null,
      customer_document: customer?.document || (manualDoc ? manualDoc.replace(/\D/g, "") : null),
      discount: saleDiscountVal,
      shipping: shippingVal,
      seller_id: sellerId || null,
      payments: payments.map((p) => ({ method: p.method, amount: parseMoney(p.amount), installments: p.method === "cartao_credito" ? parseInt(p.installments) || 1 : undefined })),
    };

    let sale;
    setBusy(true);
    try {
      const { data } = await api.post("/pos/sales", corpo);
      sale = data;
    } catch (e) {
      // Rede que caiu não é venda recusada: o pedido nem chegou a ser julgado.
      // Ela vai para a fila e sobe sozinha depois, com a MESMA identidade — o
      // que garante que uma venda gravada cuja resposta se perdeu volte como
      // ela mesma em vez de virar uma segunda.
      if (ehFalhaDeRede(e)) { segurarVenda(corpo, receipt); return; }
      toast.error(apiError(e));
      return;
    } finally { setBusy(false); }

    const fullReceipt = { ...receipt, number: sale.number, nfce: sale.invoice?.number || null };
    setLastSale(sale);
    setLastReceipt(fullReceipt);
    resetSale();
    // Impressão automática do cupom (silenciosa se o agente estiver aberto).
    printReceipt(fullReceipt, { silent: true });
    if (sale.invoice) {
      toast.success(`Venda ${sale.number} · NFC-e ${sale.invoice.number} · Comissão ${brl(sale.commission)}`);
    } else {
      // Forma de pagamento não configurada para emissão automática: pergunta ao vendedor.
      toast.success(`Venda ${sale.number} concluída · Comissão ${brl(sale.commission)}`);
      setEmitDoc(saleDoc ? maskDoc(saleDoc) : "");
      setEmitPrompt({ saleId: sale.id, number: sale.number });
    }
    barcodeRef.current?.focus();
  };

  /**
   * Transforma o carrinho em orçamento em vez de venda.
   *
   * Nada de estoque nem comissão: o orçamento só reserva o combinado com o
   * cliente. Os itens já vão vinculados ao catálogo, o que permite converter em
   * venda depois com um clique (tela de Orçamentos).
   */
  const generateQuote = async () => {
    if (!customer?.phone) {
      toast.error("Vincule um cliente com telefone — é por ele que o orçamento vai para o WhatsApp.");
      return;
    }
    // Mesma regra do finalize: só a rede no try, para não reportar erro em
    // orçamento que o servidor já gravou.
    let quote;
    setBusy(true);
    try {
      const { data } = await api.post("/quotes", {
        customer_id: customer.id,
        customer_name: customer.name,
        phone: customer.phone,
        items: cart.map((i) => ({
          product_id: i.product_id,
          variant_id: i.variant_id,
          description: i.variant_label && i.variant_label !== "Único" ? `${i.name} (${i.variant_label})` : i.name,
          qty: i.qty,
          // Orçamento leva o preço JÁ abatido: é o que foi combinado com o
          // cliente, e é esse número que ele vai comparar quando voltar.
          unit_price: i.qty > 0 ? Math.max(0, i.price - (i.discount || 0) / i.qty) : i.price,
        })),
      });
      quote = data;
    } catch (e) {
      toast.error(apiError(e));
      return;
    } finally { setBusy(false); }

    toast.success(`Orçamento ${quote.number} gerado`);
    // Abre o WhatsApp já com a mensagem e o link do PDF montados pelo backend.
    if (quote.wa_link) window.open(quote.wa_link, "_blank", "noopener");
    resetSale();
    barcodeRef.current?.focus();
  };

  /**
   * Imprime o cupom no agente local.
   *
   * `silent` cala UM caso só: a máquina não tem o agente aberto. Aí não há nada
   * a fazer e o aviso seria ruído a cada venda.
   *
   * Todo o resto é acionável e passou a avisar. Antes, qualquer falha era
   * engolida na impressão automática — e "o perfil bobina está sem impressora"
   * (que o agente devolve com `no_printer` justamente para ser resolvido em dez
   * segundos), token errado ou origem bloqueada faziam a loja vender o dia
   * inteiro sem cupom sem ninguém desconfiar. Como a venda JÁ está gravada, o
   * aviso é warning e não error: não é a venda que falhou, é o papel.
   */
  const printReceipt = async (receipt, { silent = false } = {}) => {
    const r = receipt || lastReceipt;
    if (!r) return;
    const tid = silent ? null : toast.loading("Enviando cupom para a impressora...");
    try {
      await printAgent.printReceipt(buildReceiptHtml(r), { label: `Cupom ${r.number}` });
      if (tid) toast.success("Cupom enviado para impressão", { id: tid });
    } catch (e) {
      if (silent) {
        /* Agente fechado numa máquina que NUNCA teve impressora é o caso do
           tablet que só registra venda: avisar a cada cupom seria ruído. Numa
           máquina onde a ponte já respondeu, é a impressora do balcão caída —
           e aí o silêncio é o próprio defeito, porque o cliente fica esperando
           um papel que ninguém sabe que não vem. */
        if (e.code === "agent_offline" && !agente.jaRespondeu) return;
        toast.warning(`Venda registrada, mas o cupom não saiu: ${e.message}`, { duration: 9000 });
        return;
      }
      toast.error(e.message || "Falha ao imprimir", { id: tid });
    }
  };

  /**
   * Sobe o que ficou na fila, uma venda de cada vez.
   *
   * A venda só passa a existir de verdade AQUI: é neste momento que ela ganha
   * número, baixa estoque, entra no caixa aberto e emite NFC-e — tudo com a data
   * de agora. Por isso o cupom só é impresso neste ponto, e não quando o
   * vendedor fechou a venda.
   *
   * Recusa do servidor (estoque acabou, caixa fechado) não é tentada de novo em
   * laço: fica visível na tela para alguém decidir.
   */
  const enviarFila = async () => {
    const { enviadas, falhas } = await enviarPendentes(
      async (payload) => (await api.post("/pos/sales", payload)).data,
    );
    if (!enviadas.length && !falhas.length) return;
    setFila(lerFila());

    for (const { item, venda } of enviadas) {
      const cupom = { ...item.receipt, number: venda.number, nfce: venda.invoice?.number || null };
      toast.success(`Venda em espera enviada: ${venda.number}`);
      printReceipt(cupom, { silent: true });
    }
    for (const { erro } of falhas) {
      toast.error(`Uma venda em espera foi recusada: ${erro}`, { duration: 15000 });
    }
  };

  /* A função muda de identidade a cada render (fecha sobre estado da tela), e o
     intervalo abaixo não pode ser recriado junto — a ref mantém sempre a versão
     mais nova sem que o efeito precise depender dela. */
  const enviarFilaRef = useRef(enviarFila);
  useEffect(() => { enviarFilaRef.current = enviarFila; });

  /**
   * Quando a fila é tentada de novo: ao abrir o balcão, quando o navegador avisa
   * que a rede voltou, e a cada 20s enquanto sobrar venda pendente.
   *
   * O evento `online` sozinho não basta — ele dispara quando a INTERFACE de rede
   * volta, o que acontece antes de o servidor estar alcançável de novo, e não
   * dispara em queda que não derruba a interface (servidor fora, Wi-Fi conectado
   * sem internet). O intervalo é a rede de segurança para esses dois casos.
   */
  useEffect(() => {
    const disparar = () => { enviarFilaRef.current?.(); };
    disparar();
    window.addEventListener("online", disparar);
    const t = setInterval(() => { if (contarPendentes(lerFila()) > 0) disparar(); }, 20000);

    return () => { window.removeEventListener("online", disparar); clearInterval(t); };
  }, []);

  // Emite a NFC-e manualmente quando a forma de pagamento não é automática.
  const confirmEmitNfce = async () => {
    if (emitting || !emitPrompt) return;
    setEmitting(true);
    try {
      const { data } = await api.post("/pos/invoices/emit", {
        pos_sale_id: emitPrompt.saleId, type: "nfce",
        customer_document: emitDoc ? emitDoc.replace(/\D/g, "") : undefined,
      });
      toast.success(`NFC-e ${data.number} ${data.status === "processando" ? "enviada para autorização" : "emitida"}`);
      setEmitPrompt(null); setEmitDoc("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setEmitting(false); }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F2") { e.preventDefault(); finalize(); }
      if (e.key === "F4") { e.preventDefault(); discountRef.current?.focus(); }
      if (e.key === "F6") { e.preventDefault(); if (cart.length) setSaveOpen(true); }
      if (e.key === "Escape" && !scanning) { setTerm(""); setResults([]); barcodeRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, customer, payments, discount, discountType, manualDoc, scanning]);

  const cartCount = cart.reduce((n, i) => n + i.qty, 0);
  const finalizeDisabled = cart.length === 0 || discountBlocked;

  return (
    /* Layout do balcão.
       O desenho anterior travava a altura do PDV na altura da tela e repartia o
       que sobrava entre carrinho e fechamento, cada um com scroll próprio. Isso
       funcionava numa janela alta e se desmontava em qualquer outra: numa tela
       mais baixa (ou num navegador com barra de favoritos e console abertos) a
       coluna de fechamento recebia poucos pixels e cliente, pagamento e total
       apareciam empilhados uns sobre os outros — que é o "quebra quando a tela
       é mais fina" relatado do balcão.
       Agora nada disso divide altura. A PÁGINA rola.
       DESKTOP (lg+): a coluna da esquerda (busca + venda atual) cresce à
       vontade, e a busca empurra só o carrinho para baixo. A coluna da direita
       é o fechamento inteiro — cliente, desconto, pagamento, total, finalizar e
       orçamento —, presa no topo e com a ALTURA DA TELA: ela nunca sai de vista
       e nunca é espremida, porque não disputa altura com nada.
       CELULAR: uma coluna só, na ordem natural (busca, carrinho, fechamento),
       com a barra de total fixa no rodapé. */
    <div className="flex flex-col gap-4" data-testid="pos-page">
      {cashOpen === false && (
        <div className="flex items-center justify-between gap-3 flex-wrap bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="pos-cash-closed-warning">
          <p className="text-sm text-amber-800">
            <b>Caixa fechado.</b> Abra o caixa para registrar vendas — orçamento continua liberado.
          </p>
          <Link to={cashRegisterPath} className="text-sm font-semibold text-amber-900 underline underline-offset-2">Abrir caixa</Link>
        </div>
      )}

      {/* Vendas presas pela rede. Fica no topo do balcão porque é dinheiro que
          ainda não entrou: some sozinho quando a última subir. */}
      {pendentes > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap bg-sky-50 border border-sky-200 rounded-xl px-4 py-3" data-testid="pos-queue-pending">
          <p className="text-sm text-sky-900">
            <b>{pendentes === 1 ? "1 venda em espera" : `${pendentes} vendas em espera`}.</b>{" "}
            Sem conexão com o servidor — o envio é automático assim que a rede voltar, e o cupom sai nessa hora.
          </p>
          <button onClick={() => enviarFila()} data-testid="pos-queue-retry"
            className="text-sm font-semibold text-sky-900 underline underline-offset-2">Tentar agora</button>
        </div>
      )}

      {/* Impressora fora do ar. Fica ANTES da venda, não depois: o ponto é
          descobrir com o balcão vazio, e não com o cliente esperando o papel.
          Só aparece onde a ponte já respondeu alguma vez — ver o hook. */}
      {agente.caiu && (
        <div className="flex items-center justify-between gap-3 flex-wrap bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="pos-agent-offline">
          <p className="text-sm text-amber-900">
            <b>A impressora não está respondendo.</b>{" "}
            A venda fecha normalmente, mas o cupom não vai sair. Abra o app
            <b> Ortopédica SP Impressão</b> nesta máquina.
          </p>
          <button onClick={() => agente.verificar()} data-testid="pos-agent-retry"
            className="text-sm font-semibold text-amber-900 underline underline-offset-2">
            Verificar de novo
          </button>
        </div>
      )}

      {/* Recusadas: o servidor respondeu e disse não. Não adianta insistir
          sozinho — alguém precisa abrir o caixa, repor o estoque ou desistir da
          venda. Por isso cada uma aparece com o motivo e as duas saídas. */}
      {recusadas > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 space-y-2" data-testid="pos-queue-failed">
          <p className="text-sm text-red-900">
            <b>{recusadas === 1 ? "1 venda em espera foi recusada" : `${recusadas} vendas em espera foram recusadas`}.</b>{" "}
            Elas <b>não</b> foram registradas. Resolva o motivo e tente de novo, ou descarte.
          </p>
          {fila.filter((i) => i.status === "falhou").map((item) => (
            <div key={item.client_uuid} className="flex items-center justify-between gap-3 flex-wrap text-sm border-t border-red-200 pt-2">
              <span className="text-red-800">
                <b>{brl(item.receipt?.total || 0)}</b>
                {" · "}{new Date(item.criado_em).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                {" · "}{item.erro}
              </span>
              <span className="flex gap-3">
                <button data-testid="pos-queue-item-retry"
                  onClick={() => { setFila(reenfileirar(item.client_uuid)); enviarFila(); }}
                  className="font-semibold text-red-900 underline underline-offset-2">Tentar de novo</button>
                <button data-testid="pos-queue-item-discard"
                  onClick={() => setDescartePrompt(item)}
                  className="font-semibold text-red-700 underline underline-offset-2">Descartar</button>
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        {/* ---------- Busca + Venda atual ----------
            Juntas na mesma coluna porque é a busca que alimenta o carrinho: os
            resultados aparecem imediatamente acima da lista que vão engordar, e
            empurram só ela. Antes a busca era uma faixa acima das DUAS colunas,
            e cada consulta descia o fechamento junto — o botão de finalizar
            saía da tela por causa de uma pesquisa. */}
        <div className="lg:col-span-7 flex flex-col gap-4">
          <ProductSearch ref={barcodeRef} term={term} onTermChange={setTerm} onSubmit={submitTerm}
            results={results} onAdd={promptAdd} onScan={() => setScanning(true)} />

          <CartList items={cart} count={cartCount} flashKey={flashKey}
            onChangeQty={changeQty} onRemove={(key) => setRemovePrompt(cart.find((c) => c.key === key) || null)}
            onChangeDiscount={changeItemDiscount}
            heldCount={held.length} onOpenHeld={() => setHeldOpen(true)} />
        </div>

        {/* ---------- Fechamento ----------
            `sticky` com a altura da tela: a coluna inteira acompanha a rolagem e
            tem o próprio scroll interno quando o conteúdo passa da altura
            disponível. O cálculo do 7rem é o que sobra da janela depois do
            cabeçalho do painel (h-16 = 4rem) e do respiro do <main> (p-6 em
            cima e embaixo = 3rem) — ver PanelLayout.
            Do `lg` para baixo nada disso vale: no celular a coluna é só mais um
            bloco na pilha, e a barra de total fixa no rodapé faz o papel de
            manter o valor à vista. */}
        <aside ref={checkoutRef}
          className="lg:col-span-5 flex flex-col bg-white border border-slate-200 rounded-xl lg:sticky lg:top-0 lg:h-[calc(100vh-7rem)] lg:overflow-y-auto scrollbar-thin">
          <div className="p-4 space-y-3 lg:flex-1">
            {resumeWarnings.length > 0 && <ResumeWarnings items={resumeWarnings} title={warningsTitle ?? undefined} />}

            {/* Cliente */}
            {customer ? (
              /* Vinculado o cliente, o que ele já levou daqui vem junto: era a
                 informação que o vendedor tinha de ir buscar no CRM, com o
                 cliente esperando no balcão. */
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2 text-sm" data-testid="pos-customer-chip">
                  <span className="flex items-center gap-2 text-brand-800 min-w-0">
                    <User size={15} className="shrink-0" aria-hidden />
                    <span className="truncate">{customer.name}{customer.document ? ` · ${maskDoc(customer.document)}` : ""}</span>
                  </span>
                  <Button variant="ghost" size="iconSm" onClick={() => setCustomer(null)} aria-label="Trocar cliente"
                    className="shrink-0 text-brand-500 hover:text-brand-700"><X size={15} /></Button>
                </div>
                <CustomerHistory customer={customer} onRepeat={addPurchaseToCart} />
              </div>
            ) : (
              <div className="space-y-2">
                <CustomerPicker
                  value={customer?.id || ""}
                  onChange={setCustomer}
                  placeholder="Buscar cliente (nome, CPF, telefone)"
                  testId="pos-customer"
                />
                <Input value={manualDoc} onChange={(e) => setManualDoc(maskDoc(e.target.value))}
                  aria-label="CPF/CNPJ do cliente na venda" placeholder="ou só CPF na nota (opcional)"
                  data-testid="pos-manual-doc" mono />
              </div>
            )}

            {/* VENDEDOR DA VENDA.
            
                Aparece só para quem administra, e acima do desconto porque é
                decisão de atribuição, não de preço. No balcão o terminal fica
                no login do admin e quem atendeu foi outro — antes a venda saía
                no nome errado e só dava para corrigir depois, com a comissão do
                dia torta até alguém lembrar. */}
            {podeEscolherVendedor && equipe.length > 0 && (
              <Field
                label="Vendedor da venda"
                hint={sellerId ? "A comissão vai para quem estiver selecionado aqui." : "Em branco: a venda fica no seu nome."}
              >
                <Select value={sellerId} onChange={(e) => setSellerId(e.target.value)} data-testid="pos-seller-select">
                  <option value="">Eu ({user?.name})</option>
                  {equipe.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </Select>
              </Field>
            )}

            <DiscountField ref={discountRef} type={discountType} onTypeChange={setDiscountType}
              value={discount} onChange={setDiscount} subtotal={subtotal}
              maxPercent={user?.max_discount_percent} />

            {/* Frete vem DEPOIS do desconto porque é a ordem da conta: abate a
                mercadoria, depois soma a entrega. Fora do DiscountField de
                propósito — ali dentro ele herdaria o teto de desconto do perfil,
                e cobrar entrega não é abater. */}
            <div>
              <label htmlFor="pos-shipping" className="text-eyebrow uppercase text-slate-500 block mb-1.5">
                Frete / entrega
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400 shrink-0">R$</span>
                <input
                  id="pos-shipping"
                  /* O `htmlFor` acima já associa, mas o "R$" entre o rótulo e o
                     campo faz a regra do lint perder o par. O aria-label também
                     é melhor no leitor de tela: ele anuncia a unidade, que
                     visualmente está no símbolo ao lado. */
                  aria-label="Valor do frete em reais"
                  value={shipping}
                  onChange={(e) => setShipping(e.target.value)}
                  inputMode="decimal"
                  placeholder="0,00"
                  data-testid="pos-shipping-input"
                  className="flex-1 h-11 md:h-10 px-3 rounded-lg border border-slate-200 text-base md:text-sm font-mono tabular-nums focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Soma no total e não entra na comissão — entrega é repasse, não mercadoria.
              </p>
            </div>

            <PaymentSection payments={payments} remaining={remaining}
              onAdd={addPayment} onChange={setPayment} onRemove={removePayment}
              cashPortion={cashPortion} received={received} onReceivedChange={setReceived} change={change} />
          </div>

          <SaleTotals subtotal={subtotal} discount={discountVal} shipping={shippingVal} total={total}
            cashOpen={cashOpen} cashRegisterPath={cashRegisterPath}
            onFinalize={finalize} onHold={() => setSaveOpen(true)} onQuote={generateQuote}
            canFinalize={!finalizeDisabled} hasItems={cart.length > 0} busy={busy}
            lastSale={lastSale} onPrint={() => printReceipt()} onEmitNfe={emitNfe} />
        </aside>
      </div>

      {cart.length > 0 && (
        <MobileActionBar total={total} discount={discountVal}
          cashOpen={cashOpen} cashRegisterPath={cashRegisterPath}
          canFinalize={paymentsSettled && !discountBlocked} busy={busy} onFinalize={finalize}
          onHold={() => setSaveOpen(true)} onQuote={generateQuote} hasItems={cart.length > 0}
          onGoToPayment={() => checkoutRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} />
      )}

      {scanning && <BarcodeScanner onDetect={onScanDetect} onClose={() => setScanning(false)} />}

      {addPrompt && (
        <AddItemDialog product={addPrompt.product} variant={addPrompt.variant}
          onClose={() => { setAddPrompt(null); barcodeRef.current?.focus(); }}
          onConfirm={confirmAdd} />
      )}

      {/* Tirar item é destrutivo dentro da venda: some o que já foi combinado
          com o cliente, e a lixeira fica a poucos pixels do "+" da quantidade.
          O texto nomeia o item — "remover" sem dizer o quê não é confirmação. */}
      {/* Descartar apaga uma venda que o vendedor fechou com o cliente na
          frente. Sem confirmação, um clique errado sumiria com ela sem deixar
          rastro nenhum — ela nunca chegou ao servidor. */}
      <ConfirmDialog
        open={!!descartePrompt}
        onClose={() => setDescartePrompt(null)}
        onConfirm={() => {
          setFila(removerDaFila(descartePrompt.client_uuid));
          setDescartePrompt(null);
          toast.success("Venda em espera descartada.");
        }}
        title="Descartar venda em espera"
        message={descartePrompt
          ? `Esta venda de ${brl(descartePrompt.receipt?.total || 0)} nunca foi registrada no servidor. Descartar apaga o registro dela deste computador e não tem como desfazer — se a mercadoria saiu, ela precisa ser lançada de novo.`
          : ""}
        confirmLabel="Descartar"
      />

      <ConfirmDialog
        open={!!removePrompt}
        onClose={() => setRemovePrompt(null)}
        onConfirm={() => {
          setCart((prev) => prev.filter((c) => c.key !== removePrompt.key));
          setRemovePrompt(null);
          barcodeRef.current?.focus();
        }}
        title="Remover item da venda"
        message={removePrompt
          ? `Tirar ${removePrompt.qty}x ${removePrompt.name}${removePrompt.variant_label && removePrompt.variant_label !== "Único" ? ` (${removePrompt.variant_label})` : ""} — ${brl(removePrompt.price * removePrompt.qty)} — desta venda?`
          : ""}
        confirmLabel="Remover" />

      <SaveHeldSaleModal open={saveOpen} onClose={() => setSaveOpen(false)} onSave={saveHeld}
        defaultLabel={customer?.name || ""} busy={holding} />
      <HeldSalesModal open={heldOpen} onClose={() => setHeldOpen(false)} items={held}
        onResume={resumeHeld} onDiscard={removeHeld} loadingId={resumingId} />

      <Modal open={!!emitPrompt} onClose={() => setEmitPrompt(null)} title="Emitir nota fiscal?" testId="pos-emit-nfce">
        {emitPrompt && (
          <div>
            <p className="text-sm text-slate-600">Venda <b className="font-mono">{emitPrompt.number}</b> concluída — o cupom foi enviado para impressão. Deseja emitir a <b>NFC-e</b> desta venda?</p>
            <Field label="CPF/CNPJ na nota" hint="Opcional — em branco sai como consumidor não identificado." className="mt-4">
              <Input value={emitDoc} onChange={(e) => setEmitDoc(maskDoc(e.target.value))} placeholder="CPF/CNPJ do cliente"
                data-testid="pos-emit-doc" autoFocus mono />
            </Field>
            <div className="flex gap-3 mt-5">
              <Button variant="secondary" block onClick={() => setEmitPrompt(null)} data-testid="pos-emit-skip">Não emitir</Button>
              <Button block loading={emitting} onClick={confirmEmitNfce} data-testid="pos-emit-confirm">Emitir NFC-e</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
